import { createSlice } from "@reduxjs/toolkit";
import store, { StoreDispatch } from "src/page/store";
import {
  UpdateWalletInputType,
  WalletPreferenceRequest,
  changeWalletPreference,
  connectCardToWallet,
  connectUserToWallet,
  getWalletCards,
  getWalletDetail,
  getWalletUsers,
  removeCardFromWallet,
  removeUserFromWallet,
  removeWalletPreference,
  updateWallet,
  toggleWalletActivation,
  toggleDefaultWallet,
  downloadWalletStatement,
  DownloadWalletStatementRequest
} from "src/network/graphql/walletService";
import { immediateToast } from "izitoast-react";
import { CardSearchRequest, cardSearch } from "src/network/graphql/cardService";
import { searchUser, searchOrganisationUsers, SearchUserRequest } from "src/network/graphql/userService";
import { Users } from "src/page/users/usersSlice";
import { Card } from "src/page/cards/cardSlice";
import { RemoveWalletPreferenceKey, Status, User } from "src/types/commonTypes";
import { Transaction } from "src/page/transactions/transactionSlice";
import {
  TransactionRequest,
  getAllInternalTransaction,
  getAllTransaction,
  getMerchant
} from "src/network/graphql/transactionService";
import { EMPTY_STRING, CAN_ACCESS_ALL_USERS_IN_ORG } from "src/components/constant/constant";
import { checkPermission, formatFullName, mapApiUser } from "src/utils";
import { showReportStatusPopup } from "src/components/UI/reportStatusPopup";

export type WalletSupporter = {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role?: string;
  status?: boolean;
};

export type WalletCard = {
  id: number;
  cardNumber: string;
  cardStatus: Status;
  accessType?: string;
  accountNumber?: string;
  cardHolderName?: string;
  object_id?: string;
  object_type?: string;
  created_at?: string;
  user?: {
    id?: number;
    fullName?: string;
    email?: string;
    role?: string;
    firstName?: string;
    lastName?: string;
  };
};

export type WalletDetailData = {
  wallet: {
    id: number;
    displayId: string;
    participant?: {
      id: number;
      displayId: string;
      fullName: string;
      firstName: string;
      lastname: string;
      email: string;
      phoneNumber: string;
      role: string;
      address: string;
      city: string;
      state: string;
      zipcode: string;
    } | null;
    group: {
      group_id: string;
      name: string;
      group_type: string;
    } | null;
    walletType: string;
    status: boolean;
    description: string;
    balance: number;
    name: string;
    createdAt?: string;
    defaultWallet: boolean;
    organisationId: number;
    repeatPay: boolean;
    repeatPayBalance: number;
    useWholeBalance: boolean;
    preferences: {
      cashWithdrawalAllowed: boolean;
      maxAllowedLimit: number;
      preferenceId: string;
      spendFrequency: string;
      limit_resets_at: string;
      spendLimit: number;
      blockedMerchants: string[];
      blockedMccCategories: string[];
      approvers: string[];
      verifiers: string[];
      approverUsers: User[];
      verifierUsers: User[];
      requireAllApprovers: boolean;
      requireAllVerifiers: boolean;
      isTransferAllowed: boolean;
      expenses_since_last_reset: number;
      next_reset_date: string;
      xero_account_id: string;
      xero_tax_type: string;
      xero_tracking_category_mapping: {
        xero_tracking_category_id: string;
        xero_tracking_option_id: string;
      }[];
      gl_code_id: string;
      category_ids: string[];
    };
  };
};

export const initialWalletDetail: WalletDetailData = {
  wallet: {
    id: 0,
    displayId: EMPTY_STRING,
    participant: null,
    group: null,
    status: false,
    walletType: EMPTY_STRING,
    description: EMPTY_STRING,
    balance: 0,
    name: EMPTY_STRING,
    defaultWallet: false,
    organisationId: 0,
    repeatPay: false,
    repeatPayBalance: 0,
    useWholeBalance: false,
    preferences: {
      cashWithdrawalAllowed: false,
      maxAllowedLimit: 0,
      limit_resets_at: EMPTY_STRING,
      preferenceId: EMPTY_STRING,
      spendFrequency: "NONE",
      spendLimit: 0,
      blockedMerchants: [],
      blockedMccCategories: [],
      approvers: [],
      verifiers: [],
      approverUsers: [],
      verifierUsers: [],
      requireAllApprovers: false,
      requireAllVerifiers: false,
      isTransferAllowed: true,
      expenses_since_last_reset: 0,
      next_reset_date: EMPTY_STRING,
      xero_account_id: EMPTY_STRING,
      xero_tax_type: EMPTY_STRING,
      xero_tracking_category_mapping: [],
      gl_code_id: EMPTY_STRING,
      category_ids: []
    }
  }
};

type Merchant = {
  merchant_id: string;
  name: string;
  mcc: string;
  mcc_category: string;
  external_id: string;
};

const walletDetailSlice = createSlice({
  name: "walletDetail",
  initialState: {
    walletDetail: initialWalletDetail,
    walletDetailLoading: false,

    supporters: [] as WalletSupporter[],
    supportersLoading: false,

    walletCards: [] as WalletCard[],
    walletCardsLoading: false,

    transList: { trans: [] as Transaction[], total: 0 },
    transLoading: false,

    depositList: { list: [] as Transaction[], total: 0 },
    depositLoading: false,

    users: { list: [] as Users[], total: 0 },
    userSearchLoading: false,

    cards: { list: [] as Card[], total: 0 },
    cardSearchLoading: false,

    merchant: [] as Merchant[],
    merchantLoading: false,
    merchantFetched: false,

    downloadStatementLoading: false,

    error: null as unknown
  },
  reducers: {
    resetWalletDetail: (state) => {
      state.walletDetail = initialWalletDetail;
      state.walletDetailLoading = false;
      state.supporters = [];
      state.supportersLoading = false;
      state.walletCards = [];
      state.walletCardsLoading = false;
      state.transList = { trans: [], total: 0 };
      state.transLoading = false;
      state.depositList = { list: [], total: 0 };
      state.depositLoading = false;
      state.users = { list: [], total: 0 };
      state.userSearchLoading = false;
      state.cards = { list: [], total: 0 };
      state.cardSearchLoading = false;
      state.merchant = [];
      state.merchantLoading = false;
      state.merchantFetched = false;
      state.downloadStatementLoading = false;
      state.error = null;
    },

    clearMerchants: (state) => {
      state.merchant = [];
      state.merchantLoading = false;
      state.merchantFetched = false;
    },

    fetchWalletDetailStart: (state) => {
      state.walletDetailLoading = true;
    },
    fetchWalletDetailSuccess: (state, action) => {
      state.walletDetailLoading = false;
      state.walletDetail.wallet = action.payload.wallet ?? initialWalletDetail.wallet;
    },
    fetchWalletDetailFail: (state, action) => {
      state.walletDetailLoading = false;
      state.walletDetail = initialWalletDetail;
      state.error = action.payload;
    },

    fetchSupportersStart: (state) => {
      state.supportersLoading = true;
    },
    fetchSupportersSuccess: (state, action) => {
      state.supportersLoading = false;
      state.supporters = action.payload ?? [];
    },
    fetchSupportersFail: (state, action) => {
      state.supportersLoading = false;
      state.supporters = [];
      state.error = action.payload;
    },

    fetchWalletCardsStart: (state) => {
      state.walletCardsLoading = true;
    },
    fetchWalletCardsSuccess: (state, action) => {
      state.walletCardsLoading = false;
      state.walletCards = action.payload ?? [];
    },
    fetchWalletCardsFail: (state, action) => {
      state.walletCardsLoading = false;
      state.walletCards = [];
      state.error = action.payload;
    },

    fetchTransListStart: (state) => {
      state.transLoading = true;
    },
    fetchTransListSuccess: (state, action) => {
      state.transLoading = false;
      state.transList.trans = action.payload?.list ?? [];
      state.transList.total = action.payload?.total ?? 0;
    },
    fetchTransListFail: (state, action) => {
      state.transLoading = false;
      state.transList = { trans: [], total: 0 };
      state.error = action.payload;
    },

    fetchDepositListStart: (state) => {
      state.depositLoading = true;
    },
    fetchDepositListSuccess: (state, action) => {
      state.depositLoading = false;
      state.depositList.list = action.payload?.list ?? [];
      state.depositList.total = action.payload?.total ?? 0;
    },
    fetchDepositListFail: (state, action) => {
      state.depositLoading = false;
      state.depositList = { list: [], total: 0 };
      state.error = action.payload;
    },

    fetchUserSearchStart: (state) => {
      state.userSearchLoading = true;
    },
    fetchUserSearchSuccess: (state, action) => {
      state.userSearchLoading = false;
      state.users.list = action.payload?.list ?? [];
      state.users.total = action.payload?.total ?? 0;
    },
    fetchUserSearchFail: (state, action) => {
      state.userSearchLoading = false;
      state.users = { list: [], total: 0 };
      state.error = action.payload;
    },

    fetchCardSearchStart: (state) => {
      state.cardSearchLoading = true;
    },
    fetchCardSearchSuccess: (state, action) => {
      state.cardSearchLoading = false;
      state.cards.list = action.payload?.list ?? [];
      state.cards.total = action.payload?.total ?? 0;
    },
    fetchCardSearchFail: (state, action) => {
      state.cardSearchLoading = false;
      state.cards = { list: [], total: 0 };
      state.error = action.payload;
    },

    fetchMerchantStart: (state) => {
      state.merchantLoading = true;
    },
    fetchMerchantSuccess: (state, action) => {
      state.merchantLoading = false;
      state.merchantFetched = true;
      state.merchant = action.payload ?? [];
    },
    fetchMerchantFail: (state, action) => {
      state.merchantLoading = false;
      state.merchantFetched = true;
      state.merchant = [];
      state.error = action.payload;
    },

    walletMutationStart: (state) => {
      state.walletDetailLoading = true;
    },
    walletMutationSuccess: (state) => {
      state.walletDetailLoading = false;
    },
    walletMutationFail: (state, action) => {
      state.walletDetailLoading = false;
      state.error = action.payload;
    },

    downloadStatementStart: (state) => {
      state.downloadStatementLoading = true;
    },
    downloadStatementSuccess: (state) => {
      state.downloadStatementLoading = false;
    },
    downloadStatementFail: (state, action) => {
      state.downloadStatementLoading = false;
      state.error = action.payload;
    }
  }
});

const {
  fetchWalletDetailStart,
  fetchWalletDetailSuccess,
  fetchWalletDetailFail,
  fetchSupportersStart,
  fetchSupportersSuccess,
  fetchSupportersFail,
  fetchWalletCardsStart,
  fetchWalletCardsSuccess,
  fetchWalletCardsFail,
  fetchTransListStart,
  fetchTransListSuccess,
  fetchTransListFail,
  fetchDepositListStart,
  fetchDepositListSuccess,
  fetchDepositListFail,
  fetchUserSearchStart,
  fetchUserSearchSuccess,
  fetchUserSearchFail,
  fetchCardSearchStart,
  fetchCardSearchSuccess,
  fetchCardSearchFail,
  fetchMerchantStart,
  fetchMerchantSuccess,
  fetchMerchantFail,
  walletMutationStart,
  walletMutationSuccess,
  walletMutationFail,
  downloadStatementStart,
  downloadStatementSuccess,
  downloadStatementFail
} = walletDetailSlice.actions;

export const { resetWalletDetail, clearMerchants } = walletDetailSlice.actions;

const searchActivityForCurrentMode = async (data: TransactionRequest) => {
  if (store.getState().auth.adminPortal) {
    return (await getAllInternalTransaction(data)).data.internal_search_activity;
  }

  return (await getAllTransaction(data)).data.search_activity;
};

const structureActivityList = (listing: { activity_listing?: Array<Record<string, unknown>>; total_size?: number }) => {
  const structured = (listing?.activity_listing ?? []).map((item) => ({
    id: item.activity_id,
    name: item.activity_name,
    endDate: item.end_timestamp,
    startDate: item.start_timestamp,
    ownerId: item.owner_id,
    activityStatus: item.activity_status,
    approval_number: item.approval_number,
    data: JSON.parse(item?.data as string)
  }));

  return { list: structured, total: listing?.total_size ?? 0 };
};

export const fetchWalletDetailAction = (walletId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletDetailStart());
    try {
      const walletResponse = await getWalletDetail(walletId);
      const wallet = walletResponse.data.get_wallet;
      const structured = {
        name: wallet?.name,
        description: wallet?.description,
        balance: wallet?.balance,
        createdAt: wallet?.created_at,
        id: wallet?.wallet_id,
        displayId: wallet?.display_id,
        status: wallet?.wallet_status === "ACTIVE",
        walletType: wallet?.wallet_type,
        defaultWallet: wallet?.is_default,
        organisationId: wallet?.organisation_id,
        repeatPay: wallet?.repeat_pay,
        repeatPayBalance: wallet?.repeat_pay_balance,
        useWholeBalance: wallet?.use_whole_balance,
        preferences: {
          cashWithdrawalAllowed: wallet?.preferences?.is_cash_withdrawal_allowed,
          maxAllowedLimit: wallet?.preferences?.maximum_allowed_limit,
          preferenceId: wallet?.preferences?.preference_id,
          spendFrequency: wallet?.preferences?.spend_frequency,
          spendLimit: wallet?.preferences?.spend_limit,
          blockedMerchants: wallet?.preferences?.blocked_merchant_ids,
          blockedMccCategories: wallet?.preferences?.blocked_mcc_categories,
          approvers: wallet?.preferences?.approvers ?? [],
          verifiers: wallet?.preferences?.verifiers ?? [],
          approverUsers: (wallet?.preferences?.approver_users ?? []).map(mapApiUser),
          verifierUsers: (wallet?.preferences?.verifier_users ?? []).map(mapApiUser),
          requireAllApprovers: wallet?.preferences?.require_all_approvers ?? false,
          requireAllVerifiers: wallet?.preferences?.require_all_verifiers ?? false,
          isTransferAllowed: wallet?.preferences?.is_transfer_allowed,
          xero_account_id: wallet?.preferences?.xero_account_id,
          xero_tax_type: wallet?.preferences?.xero_tax_type,
          xero_tracking_category_mapping: wallet?.preferences?.xero_tracking_category_mapping,
          gl_code_id: wallet?.preferences?.gl_code_id,
          category_ids: wallet?.preferences?.category_ids ?? [],
          limit_resets_at: wallet?.preferences?.limit_resets_at,
          expenses_since_last_reset: wallet?.preferences?.expenses_since_last_reset,
          next_reset_date: wallet?.preferences?.next_reset_date
        },
        participant: wallet?.user
          ? {
            firstName: wallet.user.attributes?.first_name,
            lastname: wallet.user.attributes?.last_name,
            email: wallet.user.email,
            id: wallet.user.user_id,
            displayId: wallet.user.display_id
          }
          : null,
        group: wallet?.group
      };

      dispatch(fetchWalletDetailSuccess({ wallet: structured }));
      if (cb) cb();
    } catch (error) {
      dispatch(fetchWalletDetailFail(error));
    }
  };
};

export const fetchSupportersAction = (walletId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchSupportersStart());
    try {
      const response = await getWalletUsers(walletId);
      const structured = response.data.get_wallet_users.map((user: any) => ({
        id: user?.user_id,
        firstName: user?.attributes?.first_name,
        lastName: user?.attributes?.last_name,
        email: user?.email,
        role: user?.current_organisation_role,
        status: user?.is_active
      }));

      dispatch(fetchSupportersSuccess(structured));
    } catch (error) {
      dispatch(fetchSupportersFail(error));
    }
  };
};

export const fetchWalletCardsAction = (walletId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletCardsStart());
    try {
      const response = await getWalletCards(walletId);
      const structured = response.data.get_wallet_cards?.map((card: any) => ({
        id: card?.card_id,
        cardStatus: card?.card_status,
        cardNumber: card?.ppan,
        cardHolderName: card?.holder_name,
        accessType: card?.card_type,
        object_id: card?.object_id,
        object_type: card?.object_type,
        created_at: card?.created_at,
        user: {
          firstName: card?.user?.attributes?.first_name,
          lastName: card?.user?.attributes?.last_name
        }
      }));

      dispatch(fetchWalletCardsSuccess(structured));
    } catch (error) {
      dispatch(fetchWalletCardsFail(error));
    }
  };
};

export const fetchTransListAction = (data: TransactionRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchTransListStart());
    try {
      const listing = await searchActivityForCurrentMode(data);

      dispatch(fetchTransListSuccess(structureActivityList(listing)));
    } catch (err) {
      dispatch(fetchTransListFail(err));
    }
  };
};

export const fetchDepositListAction = (data: TransactionRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchDepositListStart());
    try {
      const listing = await searchActivityForCurrentMode(data);

      dispatch(fetchDepositListSuccess(structureActivityList(listing)));
    } catch (err) {
      dispatch(fetchDepositListFail(err));
    }
  };
};

export const addSupporterAction = (walletId: string, userId: string, toAll: boolean = false) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(walletMutationStart());
    try {
      await connectUserToWallet(walletId, userId, toAll);
      dispatch(walletMutationSuccess());
      immediateToast("success", { message: "User added successfully", timeout: 3000, position: "topCenter" });
    } catch (error) {
      dispatch(walletMutationFail(error));
    }
  };
};

export const removeSupporterAction = (walletId: string, userId: string, removeFromAll: boolean) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(walletMutationStart());
    try {
      await removeUserFromWallet(walletId, userId, removeFromAll);
      dispatch(walletMutationSuccess());
      immediateToast("success", { message: "User removed successfully", timeout: 3000, position: "topCenter" });
    } catch (error) {
      dispatch(walletMutationFail(error));
    }
  };
};

export const addCardAction = (walletId: string, cardId: string, connectAll: boolean = false) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(walletMutationStart());
    try {
      const response = await connectCardToWallet(walletId, cardId, connectAll);

      if (response?.data?.errors) {
        immediateToast("error", { message: response.data.errors[0]?.message, timeout: 3000, position: "topCenter" });
        dispatch(walletMutationFail(response.data.errors[0]));
      } else {
        dispatch(walletMutationSuccess());
      }
    } catch (error) {
      dispatch(walletMutationFail(error));
    }
  };
};

export const removeCardAction = (walletId: string, cardId: string, removeFromAll: boolean = false) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(walletMutationStart());
    try {
      const response = await removeCardFromWallet(walletId, cardId, removeFromAll);

      if (!response?.data?.errors) {
        dispatch(walletMutationSuccess());
        immediateToast("success", { message: "Card removed successfully", timeout: 3000, position: "topCenter" });
      } else {
        dispatch(walletMutationFail(response.data.errors[0]?.message));
        immediateToast("error", { message: response.data.errors[0]?.message, timeout: 3000, position: "topCenter" });
      }
    } catch (error) {
      dispatch(walletMutationFail(error));
    }
  };
};

export const updateWalletAction = (data: UpdateWalletInputType) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(walletMutationStart());
    try {
      await updateWallet(data);
      dispatch(walletMutationSuccess());
    } catch (error) {
      dispatch(walletMutationFail(error));
    }
  };
};

export const downloadWalletStatementAction = (data: DownloadWalletStatementRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(downloadStatementStart());
    try {
      const response = await downloadWalletStatement(data);
      const reportId = response.data.download_wallet_statement.report_id;

      dispatch(downloadStatementSuccess());
      cb?.();
      immediateToast("success", { message: "Statement requested", timeout: 3000, position: "topCenter" });
      showReportStatusPopup({
        reportId,
        fileName: "wallet statement",
        onNavigateToReports: () => {
          window.location.href = "/reports";
        }
      });
    } catch (error) {
      dispatch(downloadStatementFail(error));
    }
  };
};

export const changePreferenceAction = (
  data: WalletPreferenceRequest,
  cb?: (_updated: WalletPreferenceRequest) => void
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(walletMutationStart());
    try {
      const response = await changeWalletPreference(data);
      const apiPref = response?.data?.save_wallet_preferences;
      const updated: WalletPreferenceRequest = {
        cashWithdrawalAllowed: apiPref.is_cash_withdrawal_allowed ?? data.cashWithdrawalAllowed,
        maxAllowedLimit: apiPref.maximum_allowed_limit ?? data.maxAllowedLimit,
        spendFrequency: apiPref.spend_frequency ?? data.spendFrequency,
        limit_resets_at: apiPref.limit_resets_at ?? data.limit_resets_at,
        spendLimit: apiPref.spend_limit ?? data.spendLimit,
        walletId: data.walletId,
        blockedMerchantIds: apiPref.blocked_merchant_ids ?? data.blockedMerchantIds ?? [],
        blockedMccCategories: apiPref.blocked_mcc_categories ?? data.blockedMccCategories ?? [],
        approvers: apiPref.approvers ?? data.approvers ?? [],
        verifiers: apiPref.verifiers ?? data.verifiers ?? [],
        requireAllApprovers: apiPref.require_all_approvers ?? data.requireAllApprovers,
        requireAllVerifiers: apiPref.require_all_verifiers ?? data.requireAllVerifiers,
        isTransferAllowed: apiPref.is_transfer_allowed ?? data.isTransferAllowed,
        xero_account_id: apiPref.xero_account_id,
        xero_tax_type: apiPref.xero_tax_type,
        xero_tracking_category_mapping: apiPref.xero_tracking_category_mapping ?? [],
        gl_code_id: apiPref.gl_code_id,
        category_ids: apiPref.category_ids ?? []
      };

      dispatch(walletMutationSuccess());
      if (cb) cb(updated);
      return updated;
    } catch (err) {
      dispatch(walletMutationFail(err));
    }
  };
};

export const removePreferenceAction = (walletId: string, key: RemoveWalletPreferenceKey, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(walletMutationStart());
    try {
      await removeWalletPreference(walletId, key);
      dispatch(walletMutationSuccess());
      if (cb) cb();
      return true;
    } catch (err) {
      dispatch(walletMutationFail(err));
      throw err;
    }
  };
};

export const fetchMerchantAction = (merchantIds: string[]) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchMerchantStart());
    try {
      const response = await getMerchant(merchantIds);

      dispatch(fetchMerchantSuccess(response?.data?.get_merchants));
    } catch (error) {
      dispatch(fetchMerchantFail(error));
    }
  };
};

export const searchUsersAction = (data: SearchUserRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUserSearchStart());
    try {
      let userListing;

      if (checkPermission(CAN_ACCESS_ALL_USERS_IN_ORG)) {
        const response = await searchOrganisationUsers(data);

        userListing = response.data.search_organisation_users.user_listing;
      } else {
        const response = await searchUser(data);

        userListing = response.data.search_user.user_listing;
      }

      const structured = userListing.map((user: any) => ({
        id: user?.display_id,
        firstName: user?.attributes?.first_name,
        lastName: user?.attributes?.last_name,
        email: user?.email,
        currentOrganisationRole: user?.current_organisation_role,
        status: user?.is_active,
        userId: user?.user_id
      }));

      dispatch(fetchUserSearchSuccess({ list: structured, total: structured.length }));
    } catch (error) {
      dispatch(fetchUserSearchFail(error));
    }
  };
};

export const searchCardsAction = (data: CardSearchRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchCardSearchStart());
    try {
      const response = await cardSearch(data);
      const structured = response.data.search_card.card_listing?.map((card: any) => {
        const attrs = card.user?.attributes;
        const firstName = attrs?.first_name;
        const lastName = attrs?.last_name;
        const fullName = card.holder_name || formatFullName(firstName, lastName);

        return {
          id: card.card_id,
          bsb: card.bsb,
          cardStatus: card.card_status,
          accessType: card.card_type,
          cardNumber: card.ppan,
          cardLimit: card.card_limit,
          user: { id: card.user_id, fullName: fullName }
        };
      });

      dispatch(fetchCardSearchSuccess({ list: structured, total: response.data.search_card.total_size }));
    } catch (error) {
      dispatch(fetchCardSearchFail(error));
    }
  };
};

export { toggleWalletActivation, toggleDefaultWallet };
export type { WalletPreferenceRequest, UpdateWalletInputType, TransactionRequest, DownloadWalletStatementRequest };

export default walletDetailSlice.reducer;
